import {
	Box,
	chakra,
	Flex,
	Stack,
	useMultiStyleConfig,
} from '@chakra-ui/react';
import type { SystemStyleObject } from '@chakra-ui/styled-system';
import { TextInfoIcon } from '../../../../shared-components';
import { t } from '@transifex/native';
import { useId } from 'react';

/** Visually hide input but keep it focusable/clickable (same idea as Chakra Radio). */
const visuallyHiddenInputSx = {
	border: '0',
	clip: 'rect(0, 0, 0, 0)',
	height: '1px',
	width: '1px',
	margin: '-1px',
	padding: '0',
	overflow: 'hidden',
	whiteSpace: 'nowrap',
	position: 'absolute',
} as const;

type Option = { label: string; testId: string };

export const Toggle: React.FC<{
	setOpenInfo: (a: any) => void;
	value: number;
	onChange: (index: number) => void;
	dataTestId1: string;
	dataTestId2: string;
	dataTestId3?: string;
	dataTestId4?: string;
	tabText1: string;
	tabText2: string;
	tabText3?: string;
	tabText4?: string;
	title: string;
	tooltipText: string;
	isDisabled?: boolean;
}> = ({
	setOpenInfo,
	value,
	onChange,
	dataTestId1,
	dataTestId2,
	dataTestId3,
	dataTestId4,
	tabText1,
	tabText2,
	tabText3,
	tabText4,
	title,
	tooltipText,
	isDisabled = false,
}) => {
	const groupName = useId();
	const tabTheme = useMultiStyleConfig('Tabs', {
		variant: 'soft-rounded',
		size: 'sm',
		colorScheme: 'primary',
	});
	const tabStyles = tabTheme.tab as SystemStyleObject & {
		_selected?: SystemStyleObject;
		_focusVisible?: SystemStyleObject;
	};
	const {
		_selected: selectedStyles,
		_focusVisible: tabFocusVisible,
		...tabBaseStyles
	} = tabStyles;
	const selectedTabStyles = selectedStyles ?? {};
	const segmentFocusRing = tabFocusVisible ?? {
		zIndex: 1,
		boxShadow: 'outline',
	};

	const options: Option[] = [
		{ label: tabText1, testId: dataTestId1 },
		{ label: tabText2, testId: dataTestId2 },
	];
	if (tabText3 && dataTestId3) {
		options.push({ label: tabText3, testId: dataTestId3 });
	}
	if (tabText4 && dataTestId4) {
		options.push({ label: tabText4, testId: dataTestId4 });
	}

	return (
		<Stack maxW="100%" minW={0} spacing={2}>
			<TextInfoIcon
				text={title}
				tooltipText={tooltipText}
				onClick={() => setOpenInfo({ isOpen: true, info: 2 })}
			/>
			<Box
				borderRadius="full"
				border="1px solid"
				borderColor="chakra-border-color"
				p="1"
				w="100%"
				maxW="100%"
				minW={0}
				bgColor="chakra-body-bg"
				data-testid={`toggle-stack-${title}`}
			>
				<Flex
					role="radiogroup"
					aria-label={t(title)}
					flexWrap="nowrap"
					gap={1}
					alignItems="stretch"
					w="100%"
					minW={0}
				>
					{options.map((opt, index) => {
						const isSelected = value === index;
						return (
							<Box
								key={opt.testId}
								as="label"
								data-testid={opt.testId}
								flex="1 1 0"
								minW={0}
								h="auto"
								minH={0}
								cursor={isDisabled ? 'not-allowed' : 'pointer'}
								fontSize="sm"
								fontWeight="semibold"
								whiteSpace="normal"
								textAlign="center"
								display="flex"
								alignItems="center"
								justifyContent="center"
								position="relative"
								sx={{
									...tabBaseStyles,
									...(isSelected ? selectedTabStyles : {}),
									py: 1,
									px: 4,
									overflowWrap: 'normal',
									wordBreak: 'normal',
									hyphens: 'none',
									...(isDisabled ? { opacity: 0.4 } : {}),
									'&:has(input:focus-visible)':
										segmentFocusRing,
								}}
							>
								<chakra.input
									type="radio"
									name={groupName}
									value={String(index)}
									checked={isSelected}
									disabled={isDisabled}
									onChange={() => onChange(index)}
									sx={visuallyHiddenInputSx}
								/>
								<chakra.span display="block" w="100%">
									{t(opt.label)}
								</chakra.span>
							</Box>
						);
					})}
				</Flex>
			</Box>
		</Stack>
	);
};
